import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

// Drives the admin "OpenAI ChatGPT subscription" widget. Reads connection
// status and runs the device-code connect flow against the admin endpoints
// added in Phase 4 (server/routes/admin-subscription.js). All calls carry the
// admin's Supabase session token.
async function authedFetch(path, opts = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  return fetch(path, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
}

async function readJsonOrThrow(res) {
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export function useSubscriptionStatus() {
  const [status, setStatus] = useState(null); // { connected, account_id, expires_at, last_refresh }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await authedFetch('/api/admin/subscription/status');
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setError(j.error || `HTTP ${res.status}`);
        return;
      }
      setStatus(await res.json());
    } catch (e) {
      setError(e?.message || 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // POST /connect -> { device_auth_id, user_code, verification_url, interval }
  const connect = useCallback(async () => {
    const res = await authedFetch('/api/admin/subscription/connect', { method: 'POST' });
    return readJsonOrThrow(res);
  }, []);

  // POST /poll { device_auth_id, user_code } -> { status: 'pending' | 'connected' }
  const poll = useCallback(async ({ deviceAuthId, userCode }) => {
    const res = await authedFetch('/api/admin/subscription/poll', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ device_auth_id: deviceAuthId, user_code: userCode }),
    });
    return readJsonOrThrow(res);
  }, []);

  const disconnect = useCallback(async () => {
    const res = await authedFetch('/api/admin/subscription/disconnect', { method: 'POST' });
    await readJsonOrThrow(res);
    await refresh();
  }, [refresh]);

  return { status, loading, error, refresh, connect, poll, disconnect };
}
